import { useEffect, useState } from "react";
import api from "../services/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Loader2, Save, RotateCcw, Warehouse, Truck, MapPin, Package, Percent, Weight, Clock } from "lucide-react";
import { toast } from "sonner";
import HubPriceCard from "../components/HubPriceCard";

const stageConfig = [
  {
    key: "pickup_agent", label: "Pickup Agent (First Mile)", icon: Package, color: "blue",
    fields: [
      { key: "base_fee", label: "Base Fee", type: "number" },
      { key: "per_km", label: "Per KM Rate", type: "number" },
      { key: "min_fee", label: "Min Fee", type: "number" },
      { key: "max_fee", label: "Max Fee", type: "number" },
    ],
  },
  {
    key: "hub1", label: "Origin Hub", icon: Warehouse, color: "amber",
    fields: [
      { key: "handling_fee", label: "Handling Fee", type: "number" },
      { key: "sorting_fee", label: "Sorting Fee", type: "number" },
    ],
  },
  {
    key: "linehaul", label: "Inter-Hub Transport", icon: Truck, color: "violet",
    fields: [
      { key: "base_fee", label: "Base Fee", type: "number" },
      { key: "per_km", label: "Per KM Rate", type: "number" },
      { key: "min_fee", label: "Min Fee", type: "number" },
      { key: "max_batch_discount", label: "Max Batch Discount %", type: "number" },
    ],
  },
  {
    key: "hub2", label: "Destination Hub", icon: Warehouse, color: "teal",
    fields: [
      { key: "handling_fee", label: "Handling Fee", type: "number" },
      { key: "storage_free_hours", label: "Free Storage (hrs)", type: "number" },
      { key: "storage_per_hour", label: "Storage/Hour", type: "number" },
      { key: "max_storage_fee", label: "Max Storage Fee", type: "number" },
    ],
  },
  {
    key: "delivery_agent", label: "Delivery Agent (Last Mile)", icon: MapPin, color: "emerald",
    fields: [
      { key: "base_fee", label: "Base Fee", type: "number" },
      { key: "per_km", label: "Per KM Rate", type: "number" },
      { key: "min_fee", label: "Min Fee", type: "number" },
      { key: "max_fee", label: "Max Fee", type: "number" },
    ],
  },
  {
    key: "platform_margin", label: "Platform Margin", icon: Percent, color: "orange",
    fields: [
      { key: "percentage", label: "Margin %", type: "number" },
      { key: "fixed_amount", label: "Fixed Amount", type: "number" },
      { key: "min_margin", label: "Min Margin", type: "number" },
    ],
  },
];

const colorMap = {
  blue: "bg-blue-50 text-blue-600",
  amber: "bg-amber-50 text-amber-600",
  violet: "bg-violet-50 text-violet-600",
  teal: "bg-teal-50 text-teal-600",
  emerald: "bg-emerald-50 text-emerald-600",
  orange: "bg-orange-50 text-[#FF6B00]",
};

export default function AdminHubPricing() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewForm, setPreviewForm] = useState({
    pickup_km: 3, delivery_km: 5, hub_km: 50, weight: 2, storage_hours: 0, batch_size: 1,
  });

  const fetchConfig = async () => {
    try {
      const { data } = await api.get("/hub-pricing/config");
      setConfig(data);
    } catch {
      toast.error("Failed to load hub pricing config");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchConfig(); }, []);

  const handleFieldChange = (stageKey, fieldKey, value) => {
    setConfig(prev => ({
      ...prev,
      [stageKey]: { ...prev[stageKey], [fieldKey]: parseFloat(value) || 0 },
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put("/hub-pricing/config", config, { headers: { "Content-Type": "application/json" } });
      toast.success("Hub pricing config updated!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save config");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm("Reset all hub pricing to defaults?")) return;
    setResetting(true);
    try {
      await api.post("/hub-pricing/config/reset");
      await fetchConfig();
      toast.success("Config reset to defaults");
    } catch {
      toast.error("Failed to reset config");
    } finally {
      setResetting(false);
    }
  };

  const handlePreview = async () => {
    try {
      const { data } = await api.get("/hub-pricing/calculate", { params: {
        pickup_km: previewForm.pickup_km,
        delivery_km: previewForm.delivery_km,
        hub_km: previewForm.hub_km,
        weight: previewForm.weight,
        storage_hours: previewForm.storage_hours,
        batch_size: previewForm.batch_size,
      }});
      setPreview(data);
    } catch {
      toast.error("Preview failed");
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[#FF6B00]" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="admin-hub-pricing-page">
      <div className="flex items-center justify-between animate-fadeInUp">
        <div>
          <h1 className="font-['Outfit'] text-2xl sm:text-3xl font-bold text-slate-900">Hub Pricing Config</h1>
          <p className="text-sm text-slate-500 mt-1">Configure rates for each stage of the multi-hub logistics pipeline</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleReset} disabled={resetting} className="rounded-xl" data-testid="hub-pricing-reset-btn">
            {resetting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RotateCcw className="h-4 w-4 mr-2" />}
            Reset
          </Button>
          <Button onClick={handleSave} disabled={saving} className="bg-[#FF6B00] text-white hover:bg-[#E65A00] rounded-xl font-semibold shadow-sm" data-testid="hub-pricing-save-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
            Save Config
          </Button>
        </div>
      </div>

      {/* Stage Cards */}
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        {stageConfig.map((stage, i) => {
          const Icon = stage.icon;
          const cmap = colorMap[stage.color] || colorMap.blue;
          return (
            <div key={stage.key} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 animate-fadeInUp" style={{ animationDelay: `${i * 0.04}s` }} data-testid={`hub-config-${stage.key}`}>
              <div className="flex items-center gap-2.5 mb-4">
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${cmap}`}>
                  <Icon className="h-4.5 w-4.5" />
                </div>
                <h3 className="font-['Outfit'] text-sm font-semibold text-slate-800">{stage.label}</h3>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {stage.fields.map((f) => (
                  <div key={f.key}>
                    <Label className="text-[10px] font-medium text-slate-400 uppercase tracking-wider">{f.label}</Label>
                    <Input
                      type="number" step="any" min="0"
                      value={config?.[stage.key]?.[f.key] ?? ""}
                      onChange={(e) => handleFieldChange(stage.key, f.key, e.target.value)}
                      className="mt-1 rounded-xl h-9 text-sm border-slate-200"
                      data-testid={`hub-field-${stage.key}-${f.key}`}
                    />
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Live Preview Section */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 animate-fadeInUp" data-testid="hub-pricing-preview">
        <h3 className="font-['Outfit'] text-base font-semibold text-slate-800 mb-4">Live Pricing Preview</h3>
        <div className="grid md:grid-cols-7 gap-3 mb-4">
          <div>
            <Label className="text-[10px] font-medium text-slate-400 uppercase">Pickup KM</Label>
            <Input type="number" step="any" value={previewForm.pickup_km} onChange={(e) => setPreviewForm({...previewForm, pickup_km: parseFloat(e.target.value) || 0})} className="mt-1 rounded-xl h-9 text-sm" />
          </div>
          <div>
            <Label className="text-[10px] font-medium text-slate-400 uppercase">Hub KM</Label>
            <Input type="number" step="any" value={previewForm.hub_km} onChange={(e) => setPreviewForm({...previewForm, hub_km: parseFloat(e.target.value) || 0})} className="mt-1 rounded-xl h-9 text-sm" />
          </div>
          <div>
            <Label className="text-[10px] font-medium text-slate-400 uppercase">Delivery KM</Label>
            <Input type="number" step="any" value={previewForm.delivery_km} onChange={(e) => setPreviewForm({...previewForm, delivery_km: parseFloat(e.target.value) || 0})} className="mt-1 rounded-xl h-9 text-sm" />
          </div>
          <div>
            <Label className="text-[10px] font-medium text-slate-400 uppercase">Weight KG</Label>
            <Input type="number" step="any" value={previewForm.weight} onChange={(e) => setPreviewForm({...previewForm, weight: parseFloat(e.target.value) || 0})} className="mt-1 rounded-xl h-9 text-sm" />
          </div>
          <div>
            <Label className="text-[10px] font-medium text-slate-400 uppercase">Storage Hrs</Label>
            <Input type="number" step="any" value={previewForm.storage_hours} onChange={(e) => setPreviewForm({...previewForm, storage_hours: parseFloat(e.target.value) || 0})} className="mt-1 rounded-xl h-9 text-sm" />
          </div>
          <div>
            <Label className="text-[10px] font-medium text-slate-400 uppercase">Batch Size</Label>
            <Input type="number" min="1" value={previewForm.batch_size} onChange={(e) => setPreviewForm({...previewForm, batch_size: parseInt(e.target.value) || 1})} className="mt-1 rounded-xl h-9 text-sm" />
          </div>
          <div className="flex items-end">
            <Button onClick={handlePreview} className="bg-violet-600 text-white hover:bg-violet-700 rounded-xl h-9 w-full text-sm font-semibold" data-testid="hub-pricing-preview-btn">
              Preview
            </Button>
          </div>
        </div>
        {preview && (
          <div className="max-w-md">
            <HubPriceCard pricing={preview} isAdmin />
          </div>
        )}
      </div>
    </div>
  );
}
